import React, { useEffect, useRef, useState } from 'react';

/**
 * The "I'm not a robot" checkbox, rendered explicitly.
 *
 * Google's script normally scans the document for `.g-recaptcha` elements as it loads. React draws this form
 * after that scan has run, so the automatic path leaves an empty box; the page therefore loads the script
 * with `?render=explicit` and this component calls `grecaptcha.render` itself once both the script and the
 * container exist.
 *
 * The token lives here rather than in form state because the widget owns it: it hands one over when the
 * customer passes, takes it away when it expires, and can only be cleared by resetting the widget. The
 * parent is told about each change and keeps the last value for its submit.
 */

interface Grecaptcha {
  render: (
    container: HTMLElement,
    parameters: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
    }
  ) => number;
  reset: (widgetId?: number) => void;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

interface RecaptchaCheckboxProps {
  siteKey: string;
  /** Called with the token, or null when it expires, errors, or the widget is reset. */
  onChange: (token: string | null) => void;
  /**
   * Called when the widget could not be loaded at all, so the form can stop offering a submit the server is
   * bound to refuse. Without this the customer met two contradictory messages: the check saying it could not
   * load, and the button saying to confirm they are not a robot.
   */
  onUnavailable?: (unavailable: boolean) => void;
  className?: string;
}

export interface RecaptchaHandle {
  reset: () => void;
}

export const RecaptchaCheckbox = React.forwardRef<
  RecaptchaHandle,
  RecaptchaCheckboxProps
>(({ siteKey, onChange, onUnavailable, className }, ref) => {
  const container = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<number | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  React.useImperativeHandle(ref, () => ({
    reset: () => {
      // The token is cleared whatever grecaptcha does. It is a third party script and reset() is known to
      // throw ("Invalid reCAPTCHA client id") if the widget it was given has gone; letting that skip the
      // clear would leave a spent token in state, and a retry would send it.
      try {
        if (widgetId.current !== null && window.grecaptcha) {
          window.grecaptcha.reset(widgetId.current);
        }
      } finally {
        onChange(null);
      }
    },
  }));

  useEffect(() => {
    if (!siteKey || !container.current) return;

    let cancelled = false;
    let attempts = 0;
    let timer: number | undefined;

    // The script is loaded async at the foot of the page, so it may not have arrived yet. Poll briefly
    // rather than racing it, and give up loudly enough for the form to say the check is unavailable -
    // silently rendering nothing would leave a submit button that always fails.
    const render = () => {
      if (cancelled || widgetId.current !== null) return;

      if (window.grecaptcha?.render && container.current) {
        widgetId.current = window.grecaptcha.render(container.current, {
          sitekey: siteKey,
          callback: (token) => onChange(token),
          'expired-callback': () => onChange(null),
          'error-callback': () => onChange(null),
        });
        return;
      }

      attempts += 1;
      if (attempts > 100) {
        setUnavailable(true);
        onUnavailable?.(true);
        return;
      }

      timer = window.setTimeout(render, 100);
    };

    render();

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
    // Rendered once for a given key: re-running would draw a second widget into the same container.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) return null;

  return (
    <div className={className} data-testid="enquiry-recaptcha">
      <div ref={container} />
      {unavailable && (
        <p role="alert">
          The verification check could not be loaded. Please refresh the page
          and try again.
        </p>
      )}
    </div>
  );
});

RecaptchaCheckbox.displayName = 'RecaptchaCheckbox';
